import { RootState } from '@/store/stores'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import reportApi from '@/api/operations/reportApi'
import { FLOW_LOG_ENTRIES } from '@/constant/dynamicReportAttributes'
import { IDynamicFlowLogItem, IFlowLogItem, IPayloadFlowLogRange } from '@/types/report'

import { FlowLogData, initialState } from '@store/types/FlowLogType'

/**
 * Parse numeric string from dynamic-report API response
 * New API returns raw values that need to be divided by 10 (dL -> L conversion)
 * @param value - string value from API
 * @param divideBy - divisor for unit conversion (default: 10)
 */
const parseNumericValue = (value: string | null | undefined, divideBy = 10): number => {
  const parsed = parseInt(value ?? '0', 10)
  return isNaN(parsed) ? 0 : parsed / divideBy
}

// Transform new response format to existing IFlowLogItem format
// Filter by flowRuleIds if provided (frontend filtering since new API returns all)
const transformDynamicFlowLogResponse = (
  items: IDynamicFlowLogItem[],
  flowRuleIds?: number[],
): IFlowLogItem[] => {
  const filtered =
    flowRuleIds && flowRuleIds.length > 0
      ? items.filter((item) =>
          flowRuleIds.includes(parseInt(item.floRuleId, 10)),
        )
      : items

  return filtered.map((item) => ({
    ts: item.ts,
    type_id: parseInt(item.floRuleId, 10),
    addr: 0,
    idx: 0,
    flowruleFlowrateExp_dl_min: parseNumericValue(item.florulIExpFloDlMin),
    flowruleFlowrateMeas_dl_min: parseNumericValue(item.florulIMeasFloDlMin),
    flowruleFlowrateUnexp_dl_min: parseNumericValue(item.florulIUnexpFloDlMin),
    flowruleIsroot: 0,
  }))
}

export const fetchFlowLogData = createAsyncThunk(
  'flowLog/fetchFlowLogData',
  async (payload: IPayloadFlowLogRange, { rejectWithValue }) => {
    try {
      const { data } = await reportApi.getDynamicFlowLog({
        interfaceId: payload.interfaceId,
        fromDate: payload.fromDate,
        toDate: payload.toDate,
        entries: FLOW_LOG_ENTRIES,
        limit: 1000000,
        offset: 0,
      })

      if (!data) return null

      // Filter by flowRuleIds at frontend (new API returns all flow rules)
      const transformedItems = transformDynamicFlowLogResponse(
        data,
        payload.flowRuleIds,
      )

      return {
        id: payload.interfaceId,
        result: { items: transformedItems },
      } as FlowLogData
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const FlowLogSlice = createSlice({
  name: 'interfaceCommonData',
  initialState,
  reducers: {
    setSelectedPipeId: (state, action: PayloadAction<number>) => {
      state.selectedPipeId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFlowLogData
      .addCase(fetchFlowLogData.pending, (state, action) => {
        state.isLoadingFlowLog = true
        state.isLoadingFlowLogError = false
      })
      .addCase(fetchFlowLogData.fulfilled, (state, action) => {
        state.isLoadingFlowLog = false
        state.isLoadingFlowLogError = false
        state.currentFlowLogData = action.payload
      })
      .addCase(fetchFlowLogData.rejected, (state, action) => {
        state.isLoadingFlowLog = false
        state.isLoadingFlowLogError = true
      })
  },
})

export const { setSelectedPipeId } = FlowLogSlice.actions

export const flowLogState = (state: RootState) => state.flowLog

export default FlowLogSlice.reducer
